import React, { useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Form } from 'reactstrap';
import { mapValues, omit, isFunction } from 'lodash';
import { useList } from 'react-use';
import { format as formatDate, parseISO } from 'date-fns';

import useFormState from '../hooks/useFormState';
import Field from '../Field';
import ListForm from '../ListForm';
import { fields as productFields, unitCostHistoryItemFields } from '../../shared/models/product';

const { entries } = Object;

// unitCostHistory は保存時 effectiveDate を 'yyyy-MM-dd' 文字列で持つ。フォームでは Date 型で扱うため相互変換する。
const toFormHistory = (history = []) => history.map(_ => ({
  ..._,
  effectiveDate: _.effectiveDate ? parseISO(_.effectiveDate) : null,
}));
const toSaveHistory = (items = []) => items
  .filter(_ => _.effectiveDate != null && _.unitCost != null)
  .map(_ => ({ effectiveDate: formatDate(_.effectiveDate, 'yyyy-MM-dd'), unitCost: _.unitCost }));

export default function ProductFormModal(props) {
  const { isOpen, values, title = '商品', fields, onClickClose } = props;
  const resolvedFields = isFunction(fields) ? fields() : fields;
  // unitCostHistory はリストフォームで別途扱うので通常フィールドからは除く
  const normalFields = omit(resolvedFields, 'unitCostHistory');
  const statedFields = useFormState(values, normalFields, isOpen);
  const [historyItems, { set: setHistoryItems, updateAt: updateHistoryAt }] = useList(toFormHistory(values?.unitCostHistory));
  const showsHistory = resolvedFields.unitCostHistory && !resolvedFields.unitCostHistory.hidden?.(values || {});

  const isUnsubmittable =
    Object.values(statedFields).some(_ => !_.isValid) ||
    historyItems.some(_ => !_.isValid);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (isUnsubmittable) return;

    props.onSubmit({
      ...mapValues(statedFields, 'value'),
      ...(showsHistory ? { unitCostHistory: toSaveHistory(historyItems) } : {}),
    }, { onClickClose });
  };

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader>{title}</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          {
            entries(statedFields).map(([fieldName, fieldSetting]) => (
              <Field
                key={fieldName}
                name={fieldName}
                values={mapValues(statedFields, 'value')}
                {...fieldSetting}
              />
            ))
          }
          {
            showsHistory && (
              <div className="mt-2">
                <label className="form-label">{resolvedFields.unitCostHistory.label}</label>
                {resolvedFields.unitCostHistory.hint && (
                  <div className="form-text text-muted mb-2" style={{ whiteSpace: 'pre-line' }}>
                    {resolvedFields.unitCostHistory.hint}
                  </div>
                )}
                <ListForm
                  addButtonLabel="原価改定を追加"
                  items={historyItems}
                  renderItem={(item, itemIndex) => (
                    <HistoryItemForm
                      values={item}
                      onChange={_ => updateHistoryAt(itemIndex, { ...item, ..._ })}
                    />
                  )}
                  onChange={setHistoryItems}
                />
              </div>
            )
          }
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" outline onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" outline onClick={onSubmit} disabled={isUnsubmittable}>保存</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function HistoryItemForm({ values, onChange }) {
  const statedFields = useFormState(values, unitCostHistoryItemFields(), values);
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value)]);

  return (
    <div className="d-flex gap-2">
      {
        entries(statedFields).map(([fieldName, fieldSetting]) => (
          <div style={{ width: fieldName === 'effectiveDate' ? 200 : 150 }} key={fieldName}>
            <Field name={fieldName} {...fieldSetting} />
          </div>
        ))
      }
    </div>
  );
}
